/**
 * MRZ parser for ID cards (TD1, three lines of 30) and passports (TD3, two
 * lines of 44), per ICAO 9303. Pure: it takes recognised lines (or raw text)
 * and returns the fields with their check-digit results. It never rejects on a
 * failed check digit — it reports `valid: false` and lowers confidence, and
 * `validate` decides what to flag (the operator confirms everything anyway).
 */
import type { OcrLine, ScanField, ScannedIdentity } from '../ocr-interface'

/** The machine-readable zone is a fixed alphabet; everything else is noise. */
const MRZ_CHARS = /^[A-Z0-9<]+$/

/** Recognised lines (with boxes), plain strings, or one blob of text. */
export type MrzInput = readonly (OcrLine | string)[] | string

/** Per-field check-digit results; names have none (the composite covers them). */
export interface MrzChecks {
  readonly documentNumber: boolean
  readonly birthDate: boolean
  readonly expiry: boolean
  readonly composite: boolean
}

export interface MrzResult {
  readonly format: 'TD1' | 'TD3'
  readonly identity: ScannedIdentity
  readonly checks: MrzChecks
  /** Every applicable check digit passed. */
  readonly valid: boolean
}

/** A character's value for the check-digit sum: digits as-is, A–Z as 10–35, filler 0. */
function charValue(c: string): number {
  if (c >= '0' && c <= '9') return c.charCodeAt(0) - 48
  if (c >= 'A' && c <= 'Z') return c.charCodeAt(0) - 55
  return 0 // '<' and anything else
}

/** ICAO check digit over a field: weights 7, 3, 1 repeating, sum mod 10. */
export function checkDigit(field: string): number {
  const weights = [7, 3, 1]
  let sum = 0
  for (let i = 0; i < field.length; i++) {
    sum += charValue(field[i] ?? '<') * (weights[i % 3] ?? 1)
  }
  return sum % 10
}

function digitAt(line: string, index: number): string {
  return line[index] ?? '<'
}

/** A YYMMDD field to an ISO date, or null when it is not a real date. `birth`
 *  years resolve against a sliding window; `expiry` years are always 20xx. */
function parseMrzDate(yymmdd: string, kind: 'birth' | 'expiry'): string | null {
  if (!/^\d{6}$/.test(yymmdd)) return null
  const yy = Number(yymmdd.slice(0, 2))
  const mm = Number(yymmdd.slice(2, 4))
  const dd = Number(yymmdd.slice(4, 6))
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return null
  const nowYy = new Date().getUTCFullYear() % 100
  const year = kind === 'expiry' ? 2000 + yy : yy <= nowYy ? 2000 + yy : 1900 + yy
  const iso = `${year}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`
  // Reject impossible days (e.g. 31 February) by round-tripping through Date.
  const d = new Date(`${iso}T00:00:00Z`)
  if (Number.isNaN(d.getTime()) || d.getUTCDate() !== dd) return null
  return iso
}

/** "SURNAME<<GIVEN<NAMES<<<" → { surname, givenNames }. Missing parts are ''. */
function parseNames(field: string): { surname: string; givenNames: string } {
  const [rawSurname = '', rawGiven = ''] = field.split('<<')
  const clean = (s: string) => s.replace(/</g, ' ').replace(/\s+/g, ' ').trim()
  return { surname: clean(rawSurname), givenNames: clean(rawGiven) }
}

/**
 * The CaRGOS document kind from the MRZ's first character: a passport or an ID
 * card. Any other travel document (a visa, a residence permit) is left for the
 * operator to name: CaRGOS's IDENT is the carta d'identità, not "other" (OPS-1).
 */
function documentType(first: string): string | undefined {
  if (first === 'P') return 'PASOR' // passport
  if (first === 'I' || first === 'A' || first === 'C') return 'IDELE' // ID card
  return undefined
}

function field<T>(value: T, confidence: number, source: 'mrz'): ScanField<T> {
  return { value, confidence, source }
}

/** Pull the MRZ lines out of all OCR lines: the right length and alphabet. */
function mrzLines(input: MrzInput): string[] {
  const raw =
    typeof input === 'string'
      ? input.split(/\r?\n/)
      : input.map((l) => (typeof l === 'string' ? l : l.text))
  return raw
    .map((t) => t.toUpperCase().replace(/\s+/g, ''))
    .filter((t) => MRZ_CHARS.test(t) && (t.length === 30 || t.length === 44))
}

function parseTd3(l1: string, l2: string): MrzResult {
  const names = parseNames(l1.slice(5))
  const docNum = l2.slice(0, 9)
  const docCheck = digitAt(l2, 9)
  const nationality = l2.slice(10, 13).replace(/</g, '')
  const birth = l2.slice(13, 19)
  const birthCheck = digitAt(l2, 19)
  const expiry = l2.slice(21, 27)
  const expiryCheck = digitAt(l2, 27)
  const compositeField = l2.slice(0, 10) + l2.slice(13, 20) + l2.slice(21, 43)
  const compositeCheck = digitAt(l2, 43)

  const checks: MrzChecks = {
    documentNumber: checkDigit(docNum) === Number(docCheck),
    birthDate: checkDigit(birth) === Number(birthCheck),
    expiry: checkDigit(expiry) === Number(expiryCheck),
    composite: checkDigit(compositeField) === Number(compositeCheck),
  }
  return assemble('TD3', l1[0] ?? 'P', names, docNum, nationality, birth, expiry, checks)
}

function parseTd1(l1: string, l2: string, l3: string): MrzResult {
  const docNum = l1.slice(5, 14)
  const docCheck = digitAt(l1, 14)
  const birth = l2.slice(0, 6)
  const birthCheck = digitAt(l2, 6)
  const expiry = l2.slice(8, 14)
  const expiryCheck = digitAt(l2, 14)
  const nationality = l2.slice(15, 18).replace(/</g, '')
  const compositeField = l1.slice(5, 30) + l2.slice(0, 7) + l2.slice(8, 15) + l2.slice(18, 29)
  const compositeCheck = digitAt(l2, 29)
  const names = parseNames(l3)

  const checks: MrzChecks = {
    documentNumber: checkDigit(docNum) === Number(docCheck),
    birthDate: checkDigit(birth) === Number(birthCheck),
    expiry: checkDigit(expiry) === Number(expiryCheck),
    composite: checkDigit(compositeField) === Number(compositeCheck),
  }
  return assemble('TD1', l1[0] ?? 'I', names, docNum, nationality, birth, expiry, checks)
}

function assemble(
  format: 'TD1' | 'TD3',
  first: string,
  names: { surname: string; givenNames: string },
  docNum: string,
  nationality: string,
  birth: string,
  expiry: string,
  checks: MrzChecks,
): MrzResult {
  // `expiry` drives only the check digit: an ID card or passport expiry is not a
  // licence field, so it never reaches ScannedIdentity (the licence parser owns 4b).
  void expiry
  const valid = checks.documentNumber && checks.birthDate && checks.expiry && checks.composite
  // A passed check digit means the field read correctly; names ride on the composite.
  const conf = (ok: boolean) => (ok ? 0.95 : 0.45)
  const birthIso = parseMrzDate(birth, 'birth')
  const cleanDoc = docNum.replace(/</g, '')
  const kind = documentType(first)

  const identity: ScannedIdentity = {
    ...(names.surname ? { surname: field(names.surname, conf(checks.composite), 'mrz') } : {}),
    ...(names.givenNames
      ? { givenNames: field(names.givenNames, conf(checks.composite), 'mrz') }
      : {}),
    ...(birthIso ? { birthDate: field(birthIso, conf(checks.birthDate), 'mrz') } : {}),
    ...(nationality ? { nationality: field(nationality, conf(checks.composite), 'mrz') } : {}),
    ...(cleanDoc ? { documentNumber: field(cleanDoc, conf(checks.documentNumber), 'mrz') } : {}),
    ...(kind ? { documentType: field(kind, conf(checks.composite), 'mrz') } : {}),
  }
  return { format, identity, checks, valid }
}

/**
 * Parse the MRZ from recognised lines or raw text. Returns null when no
 * machine-readable zone is present (an EU licence front, say, has none).
 */
export function parseMrz(input: MrzInput): MrzResult | null {
  const lines = mrzLines(input)
  const td3 = lines.filter((l) => l.length === 44)
  if (td3.length >= 2) return parseTd3(td3[0] ?? '', td3[1] ?? '')
  const td1 = lines.filter((l) => l.length === 30)
  if (td1.length >= 3) return parseTd1(td1[0] ?? '', td1[1] ?? '', td1[2] ?? '')
  return null
}
